/**
 * The seam between the editor and everything that persists it.
 *
 * One direction turns the live layer stack into a `Project` plus the PNG bytes
 * for each layer; the other turns those back into runtime layers. Both sides of
 * the same translation live here so they cannot drift — a field added to the
 * snapshot and forgotten in the hydrate is a field that silently disappears the
 * next time somebody opens their work.
 *
 * Nothing here touches the network, IndexedDB, or the save schedule.
 * `save-controller.ts` owns all of that and asks for exactly one function.
 *
 * **Encoding is the expensive part.** A layer is a 585x559 RGBA canvas; that is
 * 1.3 MB raw, and PNG-encoding it is not free. `RuntimeLayer.rev` exists for
 * precisely this: it bumps when a layer's *pixels* change and deliberately does
 * not bump on reorder, rename, hide or opacity. So the cache below re-encodes
 * only what was actually painted, and a save after reordering twelve layers
 * encodes nothing at all.
 */
import {
  CANVAS,
  DEFAULT_EDGE_SETTINGS,
  type FabricFill,
  FORMAT_VERSION,
  type GarmentType,
  isBlendMode,
  type Layer,
  type Project,
} from "../../shared/format";
import { encodePng } from "../../shared/png";
import { REGIONS } from "../../shared/regions";
import type { LayerStack, RuntimeLayer } from "./layers";

/** What the archive calls this layer's pixels. */
function pixelsKey(id: string): string {
  return `${id}.png`;
}

export interface SnapshotMeta {
  title: string;
  garmentType?: GarmentType;
  createdAt?: number;
  appVersion?: string;
}

export interface ProjectSnapshot {
  project: Project;
  /** Keyed `<layerId>.png`; `packProject` writes these into `layers/`. */
  blobs: Record<string, Uint8Array>;
  /** A small picture of the composite, for the designs grid. */
  thumb?: Uint8Array;
}

/** Wide enough to recognise a design, small enough to be free. */
const THUMB_WIDTH = 256;

/**
 * A picture of the whole composite.
 *
 * WebP through a canvas, not `encodePng`. The premultiply caveat in `export.ts`
 * exists because a 2D canvas zeroes colour under transparent pixels and the
 * export pipeline deliberately writes colour there — but a thumbnail is a
 * picture, not an asset, so nothing downstream reads those bytes. Lossy and
 * canvas-encoded is right here and roughly an order of magnitude cheaper.
 *
 * Returns undefined rather than throwing: a design with no thumbnail lists with
 * its colour tint, which is the behaviour that already existed.
 */
async function renderThumb(source: HTMLCanvasElement): Promise<Uint8Array | undefined> {
  try {
    const scale = THUMB_WIDTH / source.width;
    const w = THUMB_WIDTH;
    const h = Math.max(1, Math.round(source.height * scale));
    const off = document.createElement("canvas");
    off.width = w;
    off.height = h;
    const ctx = off.getContext("2d");
    if (!ctx) return undefined;
    ctx.drawImage(source, 0, 0, w, h);

    const blob = await new Promise<Blob | null>((r) => off.toBlob(r, "image/webp", 0.8));
    if (!blob) return undefined;
    return new Uint8Array(await blob.arrayBuffer());
  } catch {
    return undefined;
  }
}

/** RGBA bytes out of a layer's own canvas, at its own size. */
function pixelsOf(canvas: HTMLCanvasElement): ImageData {
  /*
   * A zero-dimension canvas cannot be read at all — `getImageData` throws
   * IndexSizeError on a width or height of 0 — and it is reachable: an import
   * of a malformed image, or a placement built from one, produces a layer whose
   * source has no size. Throwing here aborts the whole save, so a single bad
   * layer would stop every other layer being written. Caught at the call site
   * instead, where that layer can be skipped and named.
   */
  if (canvas.width <= 0 || canvas.height <= 0) {
    throw new Error(`layer canvas has no size (${canvas.width}x${canvas.height})`);
  }
  // willReadFrequently: this is the only reader, and it runs once per changed
  // layer per save rather than per frame — the hint would cost every draw into
  // this canvas a software-backed surface to speed up a read that is rare.
  const ctx = canvas.getContext("2d");
  if (!ctx) throw new Error("2D context unavailable while encoding a layer");
  return ctx.getImageData(0, 0, canvas.width, canvas.height);
}

/**
 * The persisted shape of one runtime layer.
 *
 * `paintInsideOnly` is runtime-only on purpose: it is cut from the stored
 * schema (see the notes on `RuntimeLayer`), and writing it into the archive
 * would make it a format commitment before it has a UI.
 *
 * `faces` now has one — "Only these faces" on the layer row — so it is written.
 * Additive and absent-safe: a project saved before the field existed reads back
 * with no restriction, which is what it had.
 */
function persist(layer: RuntimeLayer): Layer {
  const common = {
    id: layer.id,
    name: layer.name,
    visible: layer.visible,
    opacity: layer.opacity,
    blendMode: layer.blendMode,
    // Omitted when off, so a project that never clipped writes the same bytes.
    ...(layer.clipToBelow ? { clipToBelow: true } : {}),
    pixelsKey: pixelsKey(layer.id),
    ...(layer.link ? { link: layer.link } : {}),
    // Omitted entirely when there is no restriction, so an unrestricted layer
    // writes the same bytes it always did.
    ...(layer.faces && layer.faces.length > 0 ? { faces: [...layer.faces] } : {}),
  };

  if (layer.kind === "placement" && layer.placement) {
    return { ...common, kind: "placement", placement: layer.placement };
  }
  /*
    The fabric record, minus its change token.

    `rev` is stripped rather than forgotten: it is only meaningful inside the
    session that produced it, so writing it would be worse than useless. On
    reopening, revs restart from zero — a stored one would either collide by
    accident, offering to recolour a layer whose pixels have nothing to do with
    that fill, or never match and hide the dot forever. hydrate() stamps the
    rev the restored layer actually gets, which is the only number that can be
    right.

    Written unconditionally from `layer.fabric` rather than through fabricOf():
    a layer painted on since the fill has a stale record, and saving it is
    correct — the same rev comparison on reload will find the pixels no longer
    match, which is exactly what it should conclude.
  */
  const { rev: _rev, ...fill } = layer.fabric ?? {};
  return {
    ...common,
    kind: "artwork",
    ...(layer.fabric ? { fabric: fill as FabricFill } : {}),
  };
}

/**
 * A snapshotter bound to one stack, holding the encode cache.
 *
 * A closure rather than a module-level map, because two stacks in one page —
 * which is what a second editor tab or a future compare view would be — must
 * not share a cache keyed only by layer id.
 */
export function createSnapshotter(stack: LayerStack, meta: () => SnapshotMeta) {
  /** layer id → the bytes we encoded, and the rev they were encoded at. */
  const cache = new Map<string, { rev: number; bytes: Uint8Array }>();
  const createdAt = Date.now();
  /**
   * The last thumbnail, and the stack state it was drawn from.
   *
   * Keyed on every layer's `rev` plus the stack's shape, because a thumbnail is
   * a picture of the *composite* — reordering or hiding a layer changes it
   * without changing any layer's pixels, which is exactly the case `rev` alone
   * deliberately ignores.
   */
  let thumbCache: { key: string; bytes: Uint8Array } | null = null;

  return {
    async snapshot(): Promise<ProjectSnapshot> {
      const runtime = stack.layers();
      const blobs: Record<string, Uint8Array> = {};
      /** Layers whose pixels could not be encoded, so they are not promised. */
      const unwritable = new Set<string>();

      for (const layer of runtime) {
        const hit = cache.get(layer.id);
        if (hit && hit.rev === layer.rev) {
          blobs[pixelsKey(layer.id)] = hit.bytes;
          continue;
        }
        try {
          const data = pixelsOf(layer.source);
          const bytes = await encodePng(data.data, layer.source.width, layer.source.height);
          cache.set(layer.id, { rev: layer.rev, bytes });
          blobs[pixelsKey(layer.id)] = bytes;
        } catch {
          /*
           * One layer failing must not cost the other nineteen.
           *
           * It is dropped from the manifest rather than listed without pixels:
           * a layer named in `layers.json` whose blob is missing is a layer
           * that reopens as a hole, and `hydrate` would have to guess whether
           * that meant "gone" or "empty". Not writing it at all is the honest
           * version of the same outcome, and it keeps the invariant the tests
           * assert — every layer in the manifest has bytes.
           */
          unwritable.add(layer.id);
        }
      }

      // Deleted layers would otherwise sit in the cache for the life of the
      // session, holding a megabyte each.
      const live = new Set(runtime.map((l) => l.id));
      for (const id of cache.keys()) if (!live.has(id)) cache.delete(id);

      /*
       * One thumbnail per visible change, not one per save.
       *
       * Drawing and encoding a 256px WebP is cheap but not free, and autosave
       * fires on a three-second debounce during continuous work. The key covers
       * order and visibility as well as pixels, so a reorder regenerates it and
       * a rename does not.
       */
      const thumbKey = runtime
        .map((l) => `${l.id}:${l.rev}:${l.visible ? 1 : 0}:${l.opacity}`)
        .join("|");
      if (!thumbCache || thumbCache.key !== thumbKey) {
        const bytes = await renderThumb(stack.canvas);
        thumbCache = bytes ? { key: thumbKey, bytes } : null;
      }

      const m = meta();
      const project: Project = {
        manifest: {
          formatVersion: FORMAT_VERSION,
          garmentType: m.garmentType ?? "shirt",
          canvas: { width: CANVAS.width, height: CANVAS.height },
          // A blank title reopens as a nameless row in the designs list, and
          // `filenameFor` would build an export called ".png".
          title: m.title.trim() || "Untitled shirt",
          createdAt: m.createdAt ?? createdAt,
          modifiedAt: Date.now(),
          appVersion: m.appVersion ?? "dev",
        },
        /*
         * Defaults, and honestly so.
         *
         * The editor has no runtime state for the palette, colourways or edge
         * settings — none of the three has a UI yet. Writing defaults is not a
         * placeholder for something lost; there is nothing there to lose. The
         * day any of them gains state, it gets read here and nothing else in
         * the pipeline changes.
         */
        palette: { slots: {} },
        layers: runtime.filter((l) => !unwritable.has(l.id)).map(persist),
        colourways: [],
        edge: { ...DEFAULT_EDGE_SETTINGS },
      };

      return { project, blobs, thumb: thumbCache?.bytes };
    },

    /** Drop the encode cache — after a hydrate, when every id is new anyway. */
    reset() {
      cache.clear();
      thumbCache = null;
    },

    /**
     * Test seam: how many layers are being held.
     *
     * Eviction is a memory claim, and memory is not observable from the
     * snapshot itself — `blobs` is rebuilt from the live stack every time, so
     * a cache that never released a deleted layer would produce byte-identical
     * output while holding roughly a megabyte per layer for the life of the
     * session. Asserting it needs this, and the alternative to this line is an
     * untestable claim. Same reasoning as `resetLocalStore()`.
     */
    cachedCount: () => cache.size,
  };
}

export type Snapshotter = ReturnType<typeof createSnapshotter>;

// --------------------------------------------------------------- hydrating

/**
 * Rebuild a stack from an unpacked archive.
 *
 * Layers come back in stored order, bottom first, which is the order the stack
 * itself keeps — so appending in sequence reproduces the original stacking.
 *
 * **Layer ids are not preserved.** `addArtwork`/`addPlacement` mint their own,
 * and the alternative is threading an id parameter through the stack's whole
 * creation path for no benefit: `pixelsKey` is re-derived from the new ids on
 * the next save, archives are whole and immutable rather than patched, and
 * nothing outside a single session holds a layer id. If something ever does,
 * this is the line that has to change.
 */
export async function hydrate(
  stack: LayerStack,
  project: Project,
  blobs: Record<string, Uint8Array>,
): Promise<{ restored: number; dropped: string[] }> {
  const dropped: string[] = [];
  let restored = 0;

  /*
   * A template of a different size is a different garment.
   *
   * `addArtwork` redraws whatever it is given at 585x559, so an archive written
   * against another canvas would be silently stretched onto this one and saved
   * back in its distorted form — a lossy conversion presented as a reopen. The
   * canvas has been one fixed size for every build so far, so this cannot
   * trigger today; it is here because the day it can, the failure is invisible.
   */
  if (
    project.manifest.canvas.width !== CANVAS.width ||
    project.manifest.canvas.height !== CANVAS.height
  ) {
    throw new Error(
      `archive canvas is ${project.manifest.canvas.width}x${project.manifest.canvas.height}, this build uses ${CANVAS.width}x${CANVAS.height}`,
    );
  }

  for (const layer of project.layers) {
    // ShadingLayer has no runtime equivalent yet; it is in the format so an
    // archive written by a later build still round-trips through this one
    // rather than failing to open.
    if (layer.kind !== "artwork" && layer.kind !== "placement") {
      dropped.push(layer.name);
      continue;
    }

    const bytes = blobs[layer.pixelsKey];
    if (!bytes) {
      // The manifest promised pixels the archive does not contain. Naming the
      // layer beats restoring a blank one under its name, which would look
      // like the work was erased rather than missing.
      dropped.push(layer.name);
      continue;
    }

    let image: ImageBitmap;
    try {
      image = await createImageBitmap(new Blob([bytes as BlobPart], { type: "image/png" }));
    } catch {
      dropped.push(layer.name);
      continue;
    }

    const created =
      layer.kind === "placement"
        ? stack.addPlacement({
            name: layer.name,
            image,
            width: image.width,
            height: image.height,
            placement: layer.placement,
          })
        : stack.addArtwork({ name: layer.name, image });

    /*
     * Applied after creation because both add* paths mint a visible, opaque,
     * normal-blend layer and only the stack may mutate one.
     *
     * Guarded, because each of these setters recomposites the whole 585x559
     * canvas. Unguarded that is four full composites per layer — an eighty-
     * composite boot for a twenty-layer design, every one of them redundant for
     * the common case where the layer already has the values the setter is
     * about to write.
     */
    if (created.visible !== layer.visible) stack.setVisible(created.id, layer.visible);
    if (created.opacity !== layer.opacity) stack.setOpacity(created.id, layer.opacity);
    /*
     * The blend mode, filtered against the modes this build knows.
     *
     * Same shape as the face restriction below, and for the same reason: a file
     * written by a newer build can name a mode this one has never heard of.
     * Refusing the project would lose a whole design over one layer's
     * appearance, and passing the string through reaches
     * `globalCompositeOperation`, which ignores an unknown value silently — the
     * layer would draw normally with nothing anywhere saying why.
     */
    const mode = isBlendMode(layer.blendMode) ? layer.blendMode : "normal";
    if (created.blendMode !== mode) stack.setBlendMode(created.id, mode);
    if (layer.clipToBelow) stack.setClipToBelow(created.id, true);
    /*
     * The face restriction, filtered against the regions this build knows.
     *
     * An archive is data from outside: a hand-edited file, or one written by a
     * future build whose template has faces this one does not. An unknown id
     * would clip the layer to a rect that does not exist — which is a layer
     * that silently shows nothing, the worst way to fail. Dropping the unknown
     * ones degrades to "restricted to the faces we do have", and dropping all
     * of them degrades to unrestricted, which is the safe end.
     */
    const faces = layer.faces?.filter((f) => f in REGIONS) ?? [];
    if (faces.length > 0) stack.setFaces(created.id, faces);
    /*
     * The fabric record, stamped with the rev this layer actually has.
     *
     * Last of the four, because the setters above bump nothing but the stamp
     * has to be the final word — and the layer was just built from the stored
     * pixels, so by construction those *are* the fill's pixels. That is the
     * whole reason the rev is not persisted: only the session that restores a
     * layer knows what its rev is.
     */
    if (layer.kind === "artwork" && layer.fabric) {
      created.fabric = { ...layer.fabric, rev: created.rev };
    }
    image.close();
    restored += 1;
  }

  return { restored, dropped };
}
